import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { date, money, moneyByCurrency, parseAmount, toInput, todayIso } from '../lib/format';
import { AccountSelect, useAccounts, useBankAccounts, useVendors } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Anomaly, Bill, BillLine, BillPayment } from '../lib/types';
import { useActorLabel } from '../components/entry';
import { Icons } from '../components/icons';
import { NeedBankAccount } from './Cash';
import { Badge, Button, ButtonLink, Card, Empty, ErrorNote, Field, Input, KeyValues, Modal, MoneyInput, PageHeader, Query, Select, SourceBadge, StatusBadge, Table, Tabs, Td, Th, Toggle, Tr } from '../components/ui';

export const billOverdue = (b: Pick<Bill, 'status' | 'dueDate'>) => (b.status === 'APPROVED' || b.status === 'PARTIALLY_PAID') && !!b.dueDate && b.dueDate < todayIso();

export function BillStatus({ b }: { b: Pick<Bill, 'status' | 'dueDate'> }) {
  if (billOverdue(b)) return <StatusBadge status="OVERDUE" label="Due" />;
  if (b.status === 'APPROVED') return <StatusBadge status="ISSUED" label="To pay" />;
  if (b.status === 'PENDING_APPROVAL') return <StatusBadge status="PENDING_APPROVAL" label="Needs approval" />;
  return <StatusBadge status={b.status} />;
}

type Tab = 'open' | 'approval' | 'draft' | 'paid' | 'all';

export function BillsPage() {
  const navigate = useNavigate();
  const vendors = useVendors();
  const { currency, can } = useSession();
  const [tab, setTab] = useState<Tab>('open');
  const filter = tab === 'open' ? { open: true } : tab === 'approval' ? { status: 'PENDING_APPROVAL' } : tab === 'draft' ? { status: 'DRAFT' } : tab === 'paid' ? { status: 'PAID' } : {};
  const q = useApi<Bill[]>(`/api/bills${qs({ ...filter, limit: 300 })}`);
  const pending = useApi<Bill[]>('/api/bills?status=PENDING_APPROVAL');
  const rows = q.data ?? [];
  const due = tab === 'open' ? moneyByCurrency(rows.map((b) => ({ amount: b.total - b.amountPaid, currency: b.currency })), currency) : null;

  return (
    <>
      <PageHeader
        title="Bills"
        description="Approved bills post Dr expense (or prepaid, when the service period spans months) / Cr AP. Prepaids amortize daily into expense."
        actions={
          can('ACCOUNTANT') && (
            <ButtonLink to="/ap/bills/new" variant="primary" icon={<Icons.Plus size={15} />}>
              New bill
            </ButtonLink>
          )
        }
      />
      <Tabs
        className="mb-4"
        value={tab}
        onChange={setTab}
        items={[
          { key: 'open', label: 'To pay' },
          { key: 'approval', label: 'Awaiting approval', count: pending.data?.length || undefined },
          { key: 'draft', label: 'Drafts' },
          { key: 'paid', label: 'Paid' },
          { key: 'all', label: 'All' },
        ]}
      />
      <Card pad={false} title={due !== null ? `Open payables: ${due}` : undefined}>
        <Query q={q}>
          {() =>
            rows.length === 0 ? (
              <Empty title={tab === 'open' ? 'Nothing to pay' : 'No bills here'}>
                {tab === 'open' && 'Approved bills wait here until they are paid.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Number</Th>
                    <Th>Vendor</Th>
                    <Th>Issued</Th>
                    <Th>Due</Th>
                    <Th>Channel</Th>
                    <Th right>Total</Th>
                    <Th right>Balance</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <Tr key={b.id} onClick={() => navigate(`/ap/bills/${b.id}`)}>
                      <Td className="font-medium">{b.number}</Td>
                      <Td>{vendors.byId.get(b.vendorId)?.name}</Td>
                      <Td className="num whitespace-nowrap">{date(b.issueDate)}</Td>
                      <Td className="num whitespace-nowrap">{date(b.dueDate)}</Td>
                      <Td>
                        <SourceBadge source={b.submissionSource} />
                      </Td>
                      <Td right>{money(b.total, b.currency)}</Td>
                      <Td right>{['PAID', 'VOID', 'REJECTED'].includes(b.status) ? '—' : money(b.total - b.amountPaid, b.currency)}</Td>
                      <Td>
                        <BillStatus b={b} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
    </>
  );
}

type AmortLine = { id: string; date: string; period: string; amount: number; status: string; journalEntryId: string | null };

export function BillPage() {
  const { id } = useParams();
  const { can } = useSession();
  const vendors = useVendors();
  const accounts = useAccounts();
  const actor = useActorLabel();
  const q = useApi<{ bill: Bill; lines: BillLine[]; schedule: AmortLine[]; payments: BillPayment[] }>(`/api/bills/${id}`);
  const anomalies = useApi<Anomaly[]>('/api/anomalies');
  const [paying, setPaying] = useState(false);
  const submit = useAction(() => api<{ bill: Bill }>(`/api/bills/${id}/submit`, { method: 'POST' }), { success: (r) => (r.bill.status === 'PENDING_APPROVAL' ? 'Sent for approval' : 'Bill approved and posted') });
  const voidIt = useAction(() => api(`/api/bills/${id}/void`, { method: 'POST' }), { success: 'Bill voided; postings reversed' });

  return (
    <Query q={q}>
      {({ bill: b, lines, schedule, payments }) => {
        const v = vendors.byId.get(b.vendorId);
        const flags = (anomalies.data ?? []).filter((a) => a.entityId === b.id);
        const amortized = schedule.filter((s) => s.status === 'POSTED').reduce((s, x) => s + x.amount, 0);
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/ap/bills" className="hover:text-ink">
                  ← Bills
                </Link>
              }
              title={
                <span className="flex items-center gap-3">
                  {v?.name} · {b.number} <BillStatus b={b} />
                </span>
              }
              description={b.memo}
              actions={
                can('ACCOUNTANT') && (
                  <>
                    {b.status === 'DRAFT' && (
                      <Button variant="primary" loading={submit.isPending} onClick={() => submit.mutate()}>
                        Submit
                      </Button>
                    )}
                    {b.status === 'PENDING_APPROVAL' && <ButtonLink to="/approvals">Go to approvals</ButtonLink>}
                    {(b.status === 'APPROVED' || b.status === 'PARTIALLY_PAID') && (
                      <Button variant="primary" icon={<Icons.Bank size={15} />} onClick={() => setPaying(true)}>
                        Pay bill
                      </Button>
                    )}
                    {b.status === 'APPROVED' && b.amountPaid === 0 && (
                      <Button variant="danger" loading={voidIt.isPending} onClick={() => voidIt.mutate()}>
                        Void
                      </Button>
                    )}
                  </>
                )
              }
            />
            {flags.map((a) => (
              <div key={a.id} className="mb-4 flex items-start gap-3 rounded-lg border border-bad/25 bg-bad-soft px-4 py-3 text-[13px] text-bad">
                <Icons.Alert size={18} />
                <div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-[12.5px] opacity-90">{a.detail}</div>
                </div>
              </div>
            ))}
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-5">
                <Card pad={false} title="Lines">
                  <Table>
                    <thead>
                      <tr>
                        <Th>Account</Th>
                        <Th>Description</Th>
                        <Th>Department</Th>
                        <Th>Service period</Th>
                        <Th right>Amount</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const a = l.accountId ? accounts.byId.get(l.accountId) : undefined;
                        return (
                          <tr key={l.id}>
                            <Td>
                              {a ? (
                                <Link to={`/accounts/${a.id}`} className="hover:text-brand hover:underline">
                                  <span className="num text-muted">{a.code}</span> {a.name}
                                </Link>
                              ) : (
                                '—'
                              )}
                              {l.prepaid && (
                                <Badge tone="info" className="ml-2">
                                  Prepaid
                                </Badge>
                              )}
                            </Td>
                            <Td muted>{l.description}</Td>
                            <Td muted>{l.department}</Td>
                            <Td muted className="whitespace-nowrap">
                              {l.serviceStart ? `${date(l.serviceStart, { year: false })} – ${date(l.serviceEnd)}` : '—'}
                            </Td>
                            <Td right>{money(l.amount, b.currency)}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <Td colSpan={4} className="border-b-0 text-right font-semibold">
                          Total
                        </Td>
                        <Td right className="border-b-0 font-semibold">
                          {money(b.total, b.currency)}
                        </Td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card>
                {schedule.length > 0 && (
                  <Card title="Prepaid amortization" subtitle={`${money(amortized, b.currency)} of ${money(schedule.reduce((s, x) => s + x.amount, 0), b.currency)} expensed`} pad={false}>
                    <Table dense>
                      <thead>
                        <tr>
                          <Th>Period</Th>
                          <Th>Posts on</Th>
                          <Th right>Amount</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedule.map((s) => (
                          <tr key={s.id}>
                            <Td>{s.period}</Td>
                            <Td className="num">{date(s.date)}</Td>
                            <Td right>{money(s.amount, b.currency)}</Td>
                            <Td>{s.journalEntryId ? <Link to={`/journal/${s.journalEntryId}`}><StatusBadge status={s.status} /></Link> : <StatusBadge status={s.status} />}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>
                )}
                <Card title="Payments" pad={false}>
                  {payments.length ? (
                    <Table dense>
                      <thead>
                        <tr>
                          <Th>Date</Th>
                          <Th>Reference</Th>
                          <Th right>Amount</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id}>
                            <Td className="num">{date(p.date)}</Td>
                            <Td muted>{p.journalEntryId ? <Link to={`/journal/${p.journalEntryId}`} className="hover:text-brand hover:underline">{p.reference ?? 'Payment'}</Link> : p.reference}</Td>
                            <Td right>{money(p.amount, p.currency)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Empty title="Not paid yet" />
                  )}
                </Card>
              </div>
              <Card title="Details">
                <KeyValues
                  cols={1}
                  items={[
                    ['Vendor', v ? <Link to={`/ap/vendors/${v.id}`} className="text-brand hover:underline">{v.name}</Link> : '—'],
                    ['Issue date', date(b.issueDate)],
                    ['Due date', date(b.dueDate)],
                    ['Balance', money(b.total - b.amountPaid, b.currency)],
                    ['Journal entry', b.journalEntryId ? <Link to={`/journal/${b.journalEntryId}`} className="text-brand hover:underline">View posting →</Link> : 'Posts when approved'],
                    ['Channel', <SourceBadge key="s" source={b.submissionSource} />],
                    ['Entered by', actor(b.createdBy)],
                  ]}
                />
              </Card>
            </div>
            {paying && <PayBillModal bill={b} onClose={() => setPaying(false)} />}
          </>
        );
      }}
    </Query>
  );
}

function PayBillModal({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const banks = useBankAccounts();
  const options = banks.active.filter((b) => b.currency === bill.currency);
  const [bankId, setBankId] = useState('');
  const [d, setD] = useState(todayIso());
  const [amount, setAmount] = useState(toInput(bill.total - bill.amountPaid, bill.currency));
  const [reference, setReference] = useState('');
  const minor = parseAmount(amount, bill.currency);
  const pay = useAction(() => api(`/api/bills/${bill.id}/pay`, { body: { bankAccountId: bankId || options[0]?.id, date: d, amount: minor, reference: reference || undefined } }), { success: 'Payment posted: Dr AP, Cr Cash' });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Pay ${bill.number}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!minor || minor <= 0 || !options.length} loading={pay.isPending} onClick={() => pay.mutate(undefined, { onSuccess: onClose })}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Paid from" className="col-span-2">
          {options.length || banks.isLoading ? (
            <Select value={bankId || options[0]?.id || ''} onChange={(e) => setBankId(e.target.value)}>
              {options.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.mask ? ` ··${b.mask}` : ''}
                </option>
              ))}
            </Select>
          ) : (
            <NeedBankAccount currency={bill.currency} />
          )}
        </Field>
        <Field label="Date">
          <Input type="date" value={d} onChange={(e) => setD(e.target.value)} />
        </Field>
        <Field label="Amount">
          <MoneyInput currency={bill.currency} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Reference" className="col-span-2">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ACH or check number" />
        </Field>
      </div>
      {pay.error && <ErrorNote error={pay.error} className="mt-3" />}
    </Modal>
  );
}

interface DraftBillLine {
  key: number;
  accountId: string;
  description: string;
  department: string;
  amount: string;
  serviceStart: string;
  serviceEnd: string;
}
let bk = 0;
const blankBill = (accountId = '', department = ''): DraftBillLine => ({ key: ++bk, accountId, description: '', department, amount: '', serviceStart: '', serviceEnd: '' });

export function NewBillPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vendors = useVendors();
  const [vendorId, setVendorId] = useState(params.get('vendorId') ?? '');
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [memo, setMemo] = useState('');
  const [submit, setSubmit] = useState(true);
  const [lines, setLines] = useState<DraftBillLine[]>([blankBill()]);
  const vendor = vendors.byId.get(vendorId);
  const currency = vendor?.currency ?? 'USD';
  const parsed = lines.map((l) => ({ ...l, minor: parseAmount(l.amount, currency) }));
  const total = parsed.reduce((s, l) => s + (l.minor ?? 0), 0);
  const valid = !!vendor && number.trim() && parsed.every((l) => l.minor && l.minor > 0 && l.accountId);
  const create = useAction(
    () =>
      api<{ bill: Bill }>('/api/bills', {
        body: {
          vendorId,
          number: number.trim(),
          issueDate,
          dueDate: dueDate || undefined,
          memo: memo || undefined,
          submit,
          lines: parsed.map((l) => ({ accountId: l.accountId, description: l.description || undefined, amount: l.minor, department: l.department || undefined, serviceStart: l.serviceStart || undefined, serviceEnd: l.serviceEnd || undefined })),
        },
      }),
    { success: (r) => (r.bill.status === 'PENDING_APPROVAL' ? 'Bill sent for approval' : r.bill.status === 'DRAFT' ? 'Draft saved' : 'Bill approved and posted') },
  );
  const up = (key: number, patch: Partial<DraftBillLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const pickVendor = (id: string) => {
    setVendorId(id);
    const v = vendors.byId.get(id);
    if (v) setLines((ls) => ls.map((l) => ({ ...l, accountId: l.accountId || v.defaultExpenseAccountId || '', department: l.department || v.department || '' })));
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/ap/bills" className="hover:text-ink">
            ← Bills
          </Link>
        }
        title="New bill"
        description="Give a service period that spans more than one month and the line books to prepaid, then amortizes daily."
      />
      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Vendor" className="md:col-span-2">
            <Select value={vendorId} onChange={(e) => pickVendor(e.target.value)}>
              <option value="">Select vendor…</option>
              {vendors.list.filter((v) => !v.archivedAt || v.id === vendorId).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.currency})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Bill number" hint="Duplicates per vendor are rejected">
            <Input value={number} onChange={(e) => setNumber(e.target.value)} />
          </Field>
          <div />
          <Field label="Issue date">
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label="Due date" hint={vendor && !dueDate ? `Net ${vendor.paymentTermsDays} by default` : undefined}>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Field label="Memo" className="md:col-span-2">
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </Field>
        </div>
      </Card>
      <Card className="mt-4" pad={false}>
        <Table>
          <thead>
            <tr>
              <Th w="26%">Expense account</Th>
              <Th>Description</Th>
              <Th w="130px">Department</Th>
              <Th w="140px">Service start</Th>
              <Th w="140px">Service end</Th>
              <Th w="150px">Amount</Th>
              <Th w="36px" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <Td>
                  <AccountSelect value={l.accountId} onChange={(v) => up(l.key, { accountId: v })} types={['EXPENSE', 'ASSET']} />
                </Td>
                <Td>
                  <Input value={l.description} onChange={(e) => up(l.key, { description: e.target.value })} />
                </Td>
                <Td>
                  <Input value={l.department} onChange={(e) => up(l.key, { department: e.target.value })} />
                </Td>
                <Td>
                  <Input type="date" value={l.serviceStart} onChange={(e) => up(l.key, { serviceStart: e.target.value })} />
                </Td>
                <Td>
                  <Input type="date" value={l.serviceEnd} onChange={(e) => up(l.key, { serviceEnd: e.target.value })} />
                </Td>
                <Td>
                  <MoneyInput currency={currency} value={l.amount} onChange={(e) => up(l.key, { amount: e.target.value })} placeholder="0.00" />
                </Td>
                <Td>
                  <button onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))} className="rounded p-1 text-faint hover:text-bad" aria-label="Remove line">
                    <Icons.Trash size={15} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td colSpan={5} className="border-b-0">
                <Button size="sm" variant="ghost" icon={<Icons.Plus size={14} />} onClick={() => setLines((ls) => [...ls, blankBill(vendor?.defaultExpenseAccountId ?? '', vendor?.department ?? '')])}>
                  Add line
                </Button>
              </Td>
              <Td right className="border-b-0 text-[14px] font-semibold">
                {money(total, currency)}
              </Td>
              <Td className="border-b-0" />
            </tr>
          </tfoot>
        </Table>
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
          <div className="w-96">
            <Toggle checked={submit} onChange={setSubmit} label="Submit now" description="Posts straight away, or goes to approval if a workflow matches" />
          </div>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => navigate(`/ap/bills/${r.bill.id}`) })}>
            {submit ? 'Create and submit' : 'Save draft'}
          </Button>
        </div>
        {create.error && <ErrorNote error={create.error} className="mx-4 mb-4" />}
      </Card>
    </>
  );
}
